/**
 * Workflow Node Types for Frontend
 *
 * Type definitions for the workflow node registry system.
 */

/**
 * Execution targets for workflow nodes
 */
export enum ExecutionTarget {
  SERVER = 'server',
  ESP32 = 'esp32',
  ALL = 'all'
}

/**
 * Node categories for palette organization
 */
export enum NodeCategory {
  CONTROL = 'control',
  VARIABLES = 'variables',
  TIMING = 'timing',
  INTEGRATIONS = 'integrations',
  NOTIFICATIONS = 'notifications',
  DATABASE = 'database',
  ANALYTICS = 'analytics',
  HUMAN_WORKFLOW = 'human_workflow',
  GPIO = 'gpio',
  SENSORS = 'sensors',
  COMMUNICATION = 'communication',
  DISPLAY = 'display',
  INPUT = 'input',
  SECURITY = 'security'
}

/**
 * Port definition for node inputs/outputs
 */
export interface PortDefinition {
  name: string;
  type: 'flow' | 'data';
  dataType?: string;
  description?: string;
}

/**
 * Property types for node configuration
 */
export type PropertyType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'select'
  | 'expression'
  | 'pin'
  | 'color'
  | 'object'
  | 'array';

/**
 * Property definition for node configuration
 */
export interface PropertyDefinition {
  name: string;
  type: PropertyType;
  label: string;
  description?: string;
  default?: unknown;
  required?: boolean;
  options?: { value: unknown; label: string }[];
  dynamicOptions?: {
    source: string;
    labelField?: string;
    valueField?: string;
  };
  min?: number;
  max?: number;
  validation?: string;
  showIf?: string;
  placeholder?: string;
}

/**
 * Summary of a node definition for lists
 */
export interface NodeDefinitionSummary {
  type: string;
  name: string;
  description: string;
  category: NodeCategory;
  targets: ExecutionTarget[];
  icon: string;
  color: string;
  capabilities?: string[];
  deprecated?: boolean;
}

/**
 * Full node definition
 */
export interface NodeDefinition extends NodeDefinitionSummary {
  inputs: PortDefinition[];
  outputs: PortDefinition[];
  properties: PropertyDefinition[];
  executor?: string;
  runtimeHandler?: string;
  docsUrl?: string;
  since?: string;
}

/**
 * Nodes grouped by category
 */
export interface NodesByCategory {
  category: NodeCategory;
  label: string;
  nodes: NodeDefinitionSummary[];
}

/**
 * Category metadata
 */
export interface CategoryMetadata {
  id: string;
  label: string;
  icon: string;
  order: number;
}

/**
 * Target definition
 */
export interface TargetDefinition {
  id: ExecutionTarget;
  name: string;
  description: string;
  icon: string;
}

/**
 * Device capabilities for ESP32 validation
 */
export interface DeviceCapabilities {
  id: string;
  name: string;
  target: ExecutionTarget.ESP32;
  capabilities: {
    has_display: boolean;
    display_width?: number;
    display_height?: number;
    has_touch: boolean;
    gpio_count: number;
    gpio_map: number[];
    analog_pins: number[];
    pwm_pins: number[];
    dac_pins: number[];
    has_wifi: boolean;
    has_bluetooth: boolean;
    has_ethernet: boolean;
    flash_size: number;
    ram_size: number;
    sensors: string[];
  };
}

/**
 * Validation result for a node
 */
export interface NodeValidationResult {
  nodeType: string;
  valid: boolean;
  errors: string[];
}

/**
 * Workflow validation result
 */
export interface WorkflowValidationResult {
  valid: boolean;
  target: ExecutionTarget;
  results: NodeValidationResult[];
}

/**
 * Node registry statistics
 */
export interface NodeRegistryStats {
  total: number;
  byTarget: Record<string, number>;
  byCategory: Record<string, number>;
}

/**
 * Device Variant IDs
 * Matches backend DeviceVariantId enum
 */
export enum DeviceVariantId {
  ESP32_2_8_ILI9341 = 'esp32-2.8-ili9341',
  ESP32S3_WAVESHARE_7IN = 'esp32s3-waveshare-7in',
  ESP32_DEVKITC_VE = 'esp32-devkitc-ve',
}

/**
 * Screen Resolution Interface
 */
export interface ScreenResolution {
  width: number;
  height: number;
}

/**
 * Device Variant Interface
 */
export interface DeviceVariant {
  id: DeviceVariantId;
  name: string;
  description: string;
  resolution: ScreenResolution | null;  // null for headless devices
  capabilities: {
    has_display: boolean;
    has_touch: boolean;
    gpio_count: number;
    has_wifi: boolean;
    has_bluetooth: boolean;
    has_ble: boolean;
    flash_size_mb: number;
    psram_size_mb: number;
    gpio_map: number[];
    adc_pins: number[];
    dac_pins: number[];
    i2c_buses: number;
    spi_buses: number;
    uart_ports: number;
  };
  icon?: string;
}
